// @file The store for handling the guided templates discussion board on surface
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException } from '@@/bits/error_tracker'
import PadletApi from '@@/surface/padlet_api'
import type {
  GuidedDiscussionQuestion,
  GuidedDiscussionQuestionsResponse,
  GuidedDiscussionResponse,
  GuidedDiscussionResponsesResponse,
} from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum GenerationStatus {
  Initial = 'Initial',
  Generating = 'Generating',
  Generated = 'Generated',
  Errored = 'Errored',
}

export enum GuidedSubpanel {
  Generate = 'generate',
  Configure = 'configure',
  Post = 'post',
  Share = 'share',
}

export const useSurfaceGuidedTemplateDiscussionBoardStore = defineStore(
  'surfaceGuidedTemplateDiscussionBoardStore',
  () => {
    const activePanel = ref<GuidedSubpanel | null>(GuidedSubpanel.Generate)
    function setActivePanel(panel: GuidedSubpanel): void {
      trackEvent('Onboarding discussion board', 'Changed active panel', panel)
      if (panel === GuidedSubpanel.Post) {
        // We should generate new responses if it's the initial load, if the generate panel has changed, or if the configure panel has changed
        if (!isDiscussionResponsesGenerated.value || hasGeneratePanelChanged.value || hasConfigurePanelChanged.value) {
          void generateDiscussionResponses()
        }
      }
      activePanel.value = panel
    }

    const xEmbedPanel = ref(false)
    function setEmbedPanel(value: boolean): void {
      trackEvent('Onboarding discussion board', 'Opened embed panel')
      xEmbedPanel.value = value
    }

    const discussionTopic = ref('')
    function setDiscussionTopic(topic: string): void {
      trackEvent('Onboarding discussion board', 'Set discussion topic', topic)
      discussionTopic.value = topic
    }

    const discussionGrades = ref<string[]>([])
    function updateDiscussionGrades(grade: string): void {
      if (discussionGrades.value.includes(grade)) {
        discussionGrades.value = discussionGrades.value.filter((item) => item !== grade)
      } else {
        trackEvent('Onboarding discussion board', 'Added discussion grade', grade)
        discussionGrades.value = [...discussionGrades.value, grade]
      }
    }

    // The discussion prompt is the overall question that the teacher wants to ask the students, discussion questions are just options for the discussion prompt
    const discussionPrompt = ref('')
    function setDiscussionPrompt(prompt: string): void {
      trackEvent('Onboarding discussion board', 'Set discussion prompt', prompt)
      discussionPrompt.value = prompt
    }
    // The selected discussion prompt is the question that the teacher has selected on the Generate panel, not the active discussion prompt that the teacher can edit
    const selectedDiscussionPrompt = ref('')
    function setSelectedDiscussionPrompt(prompt: string): void {
      selectedDiscussionPrompt.value = prompt
    }
    // Track when the generate or the configuration panel has changed so we can generate/regenerate questions when user navigates to post panel with segmented control instead of the next button
    const hasGeneratePanelChanged = ref(false)
    function setHasGeneratePanelChanged(value: boolean): void {
      hasGeneratePanelChanged.value = value
    }
    const hasConfigurePanelChanged = ref(false)
    function setHasConfigurePanelChanged(value: boolean): void {
      hasConfigurePanelChanged.value = value
    }

    const discussionStyle = ref('')
    function setDiscussionStyle(type: string): void {
      trackEvent('Onboarding discussion board', 'Set discussion style', type)
      discussionStyle.value = type
    }

    const discussionQuestions = ref<GuidedDiscussionQuestion[] | null>()
    const discussionQuestionsStatus = ref<GenerationStatus>(GenerationStatus.Initial)
    const isGeneratingDiscussionQuestions = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generating,
    )
    const isDiscussionQuestionsGenerated = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generated,
    )

    async function generateDiscussionQuestions(): Promise<void> {
      trackEvent('Onboarding discussion board', 'Generated AI discussion questions')
      try {
        discussionQuestionsStatus.value = GenerationStatus.Generating
        const response: JsonAPIResponse<GuidedDiscussionQuestionsResponse> =
          await PadletApi.GuidedTemplates.generateDiscussionQuestions(discussionTopic.value, discussionGrades.value)

        discussionQuestions.value = (
          response.data as JsonAPIResource<GuidedDiscussionQuestionsResponse>
        ).attributes.questions
        discussionQuestionsStatus.value = GenerationStatus.Generated
      } catch (e) {
        discussionQuestionsStatus.value = GenerationStatus.Errored
        captureFetchException(e, { source: 'SurfaceOnboardingGuidedPanelGenerateDiscussionQuestions' })
      }
    }

    const discussionResponses = ref<GuidedDiscussionResponse[] | null>(null)
    const selectedDiscussionResponse = ref<GuidedDiscussionResponse | null>(null)
    function setSelectedDiscussionResponse(response: GuidedDiscussionResponse): void {
      selectedDiscussionResponse.value = response
    }
    const discussionResponsesStatus = ref<GenerationStatus>(GenerationStatus.Initial)
    const isGeneratingDiscussionResponses = computed(
      () => discussionResponsesStatus.value === GenerationStatus.Generating,
    )
    const isDiscussionResponsesGenerated = computed(
      () => discussionResponsesStatus.value === GenerationStatus.Generated,
    )

    async function generateDiscussionResponses(): Promise<void> {
      trackEvent('Onboarding discussion board', 'Generated AI discussion responses')
      try {
        discussionResponsesStatus.value = GenerationStatus.Generating
        const response: JsonAPIResponse<GuidedDiscussionResponsesResponse> =
          await PadletApi.GuidedTemplates.generateDiscussionResponses(
            discussionPrompt.value,
            discussionStyle.value,
            discussionTopic.value,
            discussionGrades.value,
          )
        discussionResponses.value = (
          response.data as JsonAPIResource<GuidedDiscussionResponsesResponse>
        ).attributes.responses
        discussionResponsesStatus.value = GenerationStatus.Generated
        hasConfigurePanelChanged.value = false
        hasGeneratePanelChanged.value = false
      } catch (e) {
        discussionResponsesStatus.value = GenerationStatus.Errored
        captureFetchException(e, { source: 'SurfaceOnboardingGuidedPanelGenerateDiscussionResponses' })
      }
    }

    return {
      activePanel,
      setActivePanel,

      xEmbedPanel,
      setEmbedPanel,

      discussionTopic,
      setDiscussionTopic,

      discussionGrades,
      updateDiscussionGrades,

      discussionPrompt,
      setDiscussionPrompt,
      selectedDiscussionPrompt,
      setSelectedDiscussionPrompt,

      hasConfigurePanelChanged,
      setHasConfigurePanelChanged,
      hasGeneratePanelChanged,
      setHasGeneratePanelChanged,

      discussionStyle,
      setDiscussionStyle,

      discussionQuestions,
      discussionQuestionsStatus,
      isGeneratingDiscussionQuestions,
      isDiscussionQuestionsGenerated,
      generateDiscussionQuestions,

      discussionResponses,
      selectedDiscussionResponse,
      setSelectedDiscussionResponse,
      discussionResponsesStatus,
      isGeneratingDiscussionResponses,
      isDiscussionResponsesGenerated,
      generateDiscussionResponses,
    }
  },
)
